/**
 * Timezone-aware date/time formatting utilities.
 *
 * All backend timestamps arrive as UTC ISO 8601 strings.
 * This module converts them to the user's configured timezone for display.
 *
 * The timezone is fetched once from /api/health (which returns `timezone`)
 * and cached for the lifetime of the page. The browser's own timezone is
 * used as fallback until the server responds.
 */

// ── Timezone state ──

let _timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
let _fetched = false;

/**
 * Set the display timezone (called once from Shell.tsx after /api/health).
 */
export function setTimezone(tz: string): void {
	if (tz) {
		_timezone = tz;
		_fetched = true;
	}
}

/**
 * Get the current display timezone IANA name.
 */
export function getTimezone(): string {
	return _timezone;
}

/**
 * Whether the timezone has been fetched from the server.
 * Before fetch, we use the browser's timezone as a best-effort fallback.
 */
export function isTimezoneFetched(): boolean {
	return _fetched;
}

// ── Formatters ──

/**
 * Format an ISO timestamp as a localized date+time string.
 *
 * Examples:
 *   formatDateTime("2026-04-17T14:30:00Z") → "Apr 17, 2026, 16:30"  (Europe/Stockholm)
 *   formatDateTime("2026-04-17T14:30:00Z", { seconds: true }) → "Apr 17, 2026, 16:30:00"
 */
export function formatDateTime(
	iso: string | undefined | null,
	opts?: { seconds?: boolean },
): string {
	if (!iso) return "";
	try {
		const d = new Date(iso);
		if (isNaN(d.getTime())) return iso;
		return d.toLocaleString(undefined, {
			timeZone: _timezone,
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			...(opts?.seconds ? { second: "2-digit" } : {}),
			hour12: false,
		});
	} catch {
		return iso;
	}
}

/**
 * Format an ISO timestamp as a short date+time (no year).
 *
 * Example: "Apr 17, 16:30"
 */
export function formatShortDateTime(iso: string | undefined | null): string {
	if (!iso) return "";
	try {
		const d = new Date(iso);
		if (isNaN(d.getTime())) return iso;
		return d.toLocaleString(undefined, {
			timeZone: _timezone,
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			hour12: false,
		});
	} catch {
		return iso;
	}
}

/**
 * Format an ISO timestamp as a numeric, sortable-looking date+time.
 *
 * The tabular counterpart to formatDateTime: same information, but in the
 * ISO-ish shape that suits mono / tabular-nums columns where the digits are
 * meant to line up.
 *
 * Example: "2026-04-17 16:30"  (Europe/Stockholm)
 */
export function formatNumericDateTime(iso: string | undefined | null): string {
	if (!iso) return "";
	try {
		const d = new Date(iso);
		if (isNaN(d.getTime())) return iso;
		// en-CA gives YYYY-MM-DD; en-GB + h23 gives a 00-23 clock. Formatting
		// the two halves separately avoids depending on the locale's date/time
		// separator, which is not stable across ICU versions.
		const date = new Intl.DateTimeFormat("en-CA", {
			timeZone: _timezone,
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
		}).format(d);
		const time = new Intl.DateTimeFormat("en-GB", {
			timeZone: _timezone,
			hour: "2-digit",
			minute: "2-digit",
			hourCycle: "h23",
		}).format(d);
		return `${date} ${time}`;
	} catch {
		return iso;
	}
}

/**
 * Format an ISO timestamp as a date-only string.
 *
 * Example: "Apr 17, 2026"
 */
export function formatDate(iso: string | undefined | null): string {
	if (!iso) return "";
	try {
		const d = new Date(iso);
		if (isNaN(d.getTime())) return iso;
		return d.toLocaleDateString(undefined, {
			timeZone: _timezone,
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	} catch {
		return iso;
	}
}

/**
 * Format an ISO timestamp as a time-only string.
 *
 * Example: "16:30"
 */
export function formatTime(iso: string | undefined | null): string {
	if (!iso) return "";
	try {
		const d = new Date(iso);
		if (isNaN(d.getTime())) return iso;
		return d.toLocaleTimeString(undefined, {
			timeZone: _timezone,
			hour: "2-digit",
			minute: "2-digit",
			hour12: false,
		});
	} catch {
		return iso;
	}
}

/**
 * Format an ISO timestamp as a relative time string ("2h ago", "just now").
 * Falls back to formatShortDateTime for anything older than 7 days.
 */
export function formatRelative(iso: string | undefined | null): string {
	if (!iso) return "";
	try {
		const d = new Date(iso);
		if (isNaN(d.getTime())) return iso;
		const diff = Date.now() - d.getTime();
		const seconds = Math.floor(diff / 1000);

		if (seconds < 60) return "just now";
		const minutes = Math.floor(seconds / 60);
		if (minutes < 60) return `${minutes}m ago`;
		const hours = Math.floor(minutes / 60);
		if (hours < 24) return `${hours}h ago`;
		const days = Math.floor(hours / 24);
		if (days < 7) return `${days}d ago`;

		return formatShortDateTime(iso);
	} catch {
		return iso ?? "";
	}
}

/**
 * Get today's date string (YYYY-MM-DD) in the configured timezone.
 */
export function localToday(): string {
	const now = new Date();
	// Use Intl to get date parts in the target timezone
	const parts = new Intl.DateTimeFormat("en-CA", {
		timeZone: _timezone,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	}).format(now);
	return parts; // en-CA gives YYYY-MM-DD format
}

/**
 * Get the date N days ago (YYYY-MM-DD) in the configured timezone.
 *
 * `localDaysAgo(0)` === `localToday()`, `localDaysAgo(1)` is yesterday. The
 * arithmetic is done on the LOCAL calendar day, not by subtracting 24h from an
 * instant — so it stays correct across DST folds (a "day" that is 23 or 25 real
 * hours still moves the date by exactly one).
 */
export function localDaysAgo(n: number): string {
	// Parse localToday() (already the configured-zone date) as a UTC calendar
	// anchor, step whole days on it, and re-read the date parts. UTC arithmetic
	// on a date-only anchor has no offset and no DST to trip over.
	const anchor = new Date(`${localToday()}T00:00:00Z`);
	anchor.setUTCDate(anchor.getUTCDate() - n);
	// arch:tz-exempt — canonical: `anchor` is a UTC-midnight date-only value
	// built FROM localToday(), so its toISOString() date-part IS the intended
	// configured-zone calendar date. This is the helper the guard steers TO.
	return anchor.toISOString().slice(0, 10);
}

/**
 * Format an ISO timestamp (or epoch-ms) as a "Mon YYYY" label, e.g. "Apr 2026",
 * in the configured timezone. For month/year axis labels and headers derived
 * from a real instant.
 *
 * NOTE: this is for INSTANTS. To render a "YYYY-MM" *string* the user picked
 * (a month selector), parse and format it browser-locally and consistently —
 * do NOT route it through here, or a browser west of the configured zone can
 * shift the label by a month.
 */
export function formatMonthYear(iso: string | number | undefined | null): string {
	if (iso === undefined || iso === null || iso === "") return "";
	try {
		const d = new Date(iso);
		if (isNaN(d.getTime())) return String(iso);
		return d.toLocaleDateString(undefined, {
			timeZone: _timezone,
			month: "short",
			year: "numeric",
		});
	} catch {
		return String(iso);
	}
}
